/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable class-methods-use-this */
import { ICalendarDateRange, PENDING_SCHEDULING_STATUSES } from '@models';
import * as Services from '@services';
import {
  CalendarOrder,
  CreateServiceOrder,
  DocumentDto,
  Order,
  PagedUnassignedOrderRequest,
  ServiceOrder,
  UnassignedOrder,
  UnassignedOrderCounts,
  UnassignedOrderCountsRequest,
  UnassignedOrderScope,
  UpdateOrder,
  UpdateServiceOrder,
  VehicleHistoryOrder,
} from '@services';
import { workLogMinutes } from '@utils';
import { message } from 'antd';
import dayjs from 'dayjs';
import { makeAutoObservable, runInAction } from 'mobx';

/** What decides which side-panel list, if any, a job belongs on. */
type PanelCandidate = Pick<Order, 'status'> & {
  startAt?: string | null;
  userId?: string | number | null;
};

/**
 * A saved job as a side-panel card. The panel's own fields come straight off the order; the ones
 * the API only sends for the list (a build's number and name, which a save does not return in
 * full) are kept from the card already showing, if there is one.
 */
const toPanelCard = (saved: Order, current?: UnassignedOrder): UnassignedOrder & PanelCandidate => ({
  ...current,
  id: saved.id,
  title: saved.title,
  status: saved.status,
  duration: saved.duration ?? current?.duration,
  dropOffDate: saved.dropOffDate,
  isApproval: saved.isApproval,
  requestId: saved.requestId ?? current?.requestId,
  registrationNumber: saved.registrationNumber ?? current?.registrationNumber,
  vehicleModel: saved.vehicleModel ?? current?.vehicleModel,
  fullName: saved.customer?.fullname ?? current?.fullName ?? '',
  masterBuildId: saved.masterBuildId ?? current?.masterBuildId,
  masterBuildNumber: saved.masterBuildNumber ?? current?.masterBuildNumber,
  masterBuildName: saved.masterBuildName ?? current?.masterBuildName,
  startAt: saved.startAt ? String(saved.startAt) : null,
  endAt: saved.endAt ? String(saved.endAt) : null,
  userId: saved.userId,
});

class OrderStore {
  order: Order | undefined;
  /**
   * The drop-off as it stands on the saved order. The card edits `order` in place, so this is the
   * only way to tell an edited drop-off from a saved one - and the customer must not be notified
   * about a date that has not been saved yet.
   */
  savedDropOffDate: string | undefined = undefined;
  orders: Map<string, Order> = new Map();
  totalCount: number = 0;
  loading: boolean = false;
  /**
   * Whether the last list request failed. Without it a caught error leaves the table looking
   * exactly like a garage with no jobs - which is how an ended session passed for missing data.
   */
  ordersError: boolean = false;
  maxResultCount: number = 15;
  skipCount: number = 0;
  currentPage: number = 1;

  calendarOrders: Map<string, CalendarOrder> = new Map();
  calendarTotalCount: number = 0;
  calendarLoading: boolean = false;

  // A computed, so the array keeps its identity until the map changes. FullCalendar re-parses
  // its event source whenever the `events` array it is given is a new object, and that replaces
  // a card mid-drag with the stored one - Array.from in a render did exactly that on every
  // unrelated store update.
  get calendarOrderList(): CalendarOrder[] {
    return Array.from(this.calendarOrders.values());
  }

  unAssignedItems: Map<string, UnassignedOrder> = new Map();
  unassignedCurrentPage = 1;
  unAssignedTotalCount: number = 0;
  /**
   * Two sets, because the two badges answer different questions. The Unscheduled | Unassigned
   * switch inside the panel counts what its filters are showing, so a tab never claims more than
   * the list beneath it; the Jobs tab above is the garage's backlog and stays whole.
   */
  unAssignedCounts: UnassignedOrderCounts = { unscheduled: 0, unassigned: 0 };
  unAssignedTotals: UnassignedOrderCounts = { unscheduled: 0, unassigned: 0 };
  lastUnAssignedRequest: PagedUnassignedOrderRequest = { maxResultCount: 5, skipCount: 0 };
  /** What the filtered badges were last counted under - see getUnAssignedCount. */
  lastUnAssignedCountFilter: UnassignedOrderCountsRequest = {};
  /** The count request in flight, as a key: an overtaken answer is dropped rather than shown. */
  unAssignedCountRequestKey?: string;
  unAssignedMaxResultCount: number = 5;
  unAssignedSkipCount: number = 0;
  unAssignedLoading: boolean = false;
  /** As with ordersError: an empty panel that failed to load must not read as "nothing pending". */
  unAssignedError: boolean = false;
  unAssignedTotalLoading: boolean = false;

  relatedOrders: Map<string, Order> = new Map();
  relatedCheckedIds: string[] = [];
  /** Which order the related list belongs to - a badge must not trust a list loaded for another. */
  relatedOrdersOrderId: string | undefined = undefined;

  relatedLoading = false;

  orderChanges: Services.PagedResultDto<Services.OrderChange> = {
    items: [],
    totalCount: 0,
  };
  changeSkipCount: number = 0;
  changeCurrentPage: number = 1;

  serviceOrderDocuments: Services.PagedResultDto<DocumentDto> = {
    items: [],
    totalCount: 0,
  };

  generateInvoiceOrders: Map<string, Order> = new Map();

  vehicleHistoryOrders: Map<string, VehicleHistoryOrder> = new Map();
  vehicleHistoryOrdersTotalCount: number = 0;
  vehicleHistoryOrdersLoading: boolean = false;
  vehicleHistoryOrdersSkipCount: number = 0;
  vehicleHistoryOrdersMaxResultCount: number = 15;
  vehicleHistoryOrdersCurrentPage: number = 1;

  orderNotes = new Map<string, Services.OrderNote>();
  orderNotesTotalCount: number = 0;
  orderNotesLoading: boolean = false;
  orderNoteLoading: boolean = false;
  orderNotesSkipCount: number = 0;
  orderNotesMaxResultCount: number = 5;
  orderNotesCurrentPage: number = 1;

  // Technician time entries for the open order. Not paged: an order has a handful at most and the
  // tab needs the full set to total per technician and per service line.
  orderWorkLogs = new Map<string, Services.OrderWorkLog>();
  orderWorkLogsLoading: boolean = false;
  orderWorkLogSaving: boolean = false;

  statusLoading = false;

  // Whether the order screen shows its three header cards. A per-browser convenience, so it is
  // remembered locally and never sent anywhere; storage may be unavailable, hence the guards.
  orderDetailsExpanded: boolean = (() => {
    try {
      return localStorage.getItem('orderDetailsExpanded') !== 'false';
    } catch {
      return true;
    }
  })();

  toggleOrderDetails = () => {
    runInAction(() => {
      this.orderDetailsExpanded = !this.orderDetailsExpanded;
    });
    try {
      localStorage.setItem('orderDetailsExpanded', String(this.orderDetailsExpanded));
    } catch {
      // Private window or blocked storage: the toggle still works for this session.
    }
  };

  approvalHistory = new Map<string, Services.OrderApprovalHistory>();
  approvalHistoryTotalCount: number = 0;
  approvalHistoryLoading: boolean = false;
  approvalHistorySkipCount: number = 0;
  approvalHistoryMaxResultCount: number = 10;
  approvalHistoryCurrentPage: number = 1;

  presences = new Map<string, Services.OrderPresence>();
  presenceLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  //refactored
  addOrUpdateCalendarOrder = (order: CalendarOrder) => {
    runInAction(() => {
      if (order.status === 'Canceled') {
        this.calendarOrders.delete(order.id);
      } else {
        // Merge, don't replace: the SignalR payload carries every field the notification
        // handler lists, but not the build fields, and a full replace stripped the build badge
        // off a card on every update until the next reload.
        const current = this.calendarOrders.get(order.id);
        this.calendarOrders.set(order.id, current ? { ...current, ...order } : order);
      }

      if (!this.order || this.order.id !== order.id) {
        return;
      }

      this.order = {
        ...this.order,
        ...order,
        user: {
          id: order.userId && Number(order?.userId),
          fullname: order?.userName,
        } as any,
      };
    });
  };

  updateOrdersStatus = (orders: string, status: string) => {
    runInAction(() => {
      const splited = orders.split(',');
      splited.forEach((orderId) => {
        const trimmedId = orderId.trim();
        const updatedOrder = this.calendarOrders.get(trimmedId);

        if (updatedOrder) {
          updatedOrder.status = status;
          this.calendarOrders.set(trimmedId, updatedOrder);
        }

        if (this.order?.id === trimmedId) {
          this.order.status = status;
        }
      });
    });
  };

  setOrder = (order: Order) => {
    runInAction(() => {
      // Related jobs and their ticks (for combined invoicing) belong to one order; a save of the
      // same order keeps them, opening another drops both so nothing from the last order shows.
      if (this.order?.id !== order.id) {
        this.relatedCheckedIds = [];
        this.relatedOrders.clear();
        this.relatedOrdersOrderId = undefined;
      }
      this.order = order;
      this.savedDropOffDate = order.dropOffDate ? String(order.dropOffDate) : undefined;
    });
  };

  updateOrderField = (values: Partial<Order>) => {
    if (this.order) {
      Object.assign(this.order, values);
    }
  };

  // Anchors the order's end time to start + total service duration (hours). Call on explicit
  // service changes (add/remove/duration) so the booked range follows the services; a manual
  // time edit is untouched because this is not run on load.
  syncEndFromServiceDuration = (totalDurationHours: number) => {
    if (!this.order?.startAt) return;
    this.updateOrderField({
      endAt: dayjs(this.order.startAt).add(totalDurationHours, 'hour').toISOString(),
    });
  };

  setStatusLoading = (value: boolean) => {
    runInAction(() => {
      this.statusLoading = value;
    });
  };

  //refactored
  async getCalendarItems(request: ICalendarDateRange) {
    this.setCalendarLoading(true);
    try {
      const result = await Services.OrderService.getCalendarAll(request);

      runInAction(() => {
        result.forEach((serviceOrder) => {
          this.calendarOrders.set(serviceOrder.id, serviceOrder);
        });
      });
      this.setCalendarLoading(false);
    } catch (error) {
      this.setCalendarLoading(false);
      console.error(error);
    }
  }

  //refactored
  async create(request: Services.CreateOrder) {
    this.setCalendarLoading(true);
    try {
      const result = await Services.OrderService.create(request);

      runInAction(() => {
        if (result.relationId == this.order?.relationId) {
          this.relatedOrders.set(result.id, result);
        }
        // Add the new order to the beginning of the map
        const tempArray = Array.from(this.orders.entries());
        tempArray.unshift([result.id, result]);

        // If there are more than 10 orders, remove the oldest one
        if (tempArray.length > 10) {
          tempArray.pop();
        }

        // Clear the map and add the updated list back to it
        this.orders.clear();
        tempArray.forEach(([id, order]) => {
          this.orders.set(id, order);
        });

        // Update the total count
        this.totalCount += 1;
      });

      message.success({
        content: 'New booking was successfully created!',
      });
      this.setCalendarLoading(false);
      return result;
    } catch (error: any) {
      this.setCalendarLoading(false);
      console.error(error);
      return null;
    }
  }

  buildServiceOrders(
    serviceStore: {
      selectedServices: any[];
      selectedServiceParts: Map<number, any[]>;
      tenantVat: number;
    },
    orderId?: string
  ) {
    const { selectedServices, selectedServiceParts, tenantVat } = serviceStore;

    return selectedServices.map((service: any) => ({
      title: service.name,
      price: service.price,
      discount: service.discount || 0,
      duration: service.duration === 0 ? 1 : service.duration,
      vat: service.isVat ? tenantVat : 0,
      isFixed: service.isFixed,
      note: service.note || '',
      serviceId: service.id,
      ...(orderId && { orderId }),
      extras:
        selectedServiceParts
          .get(service.id)
          ?.map((part: any) => ({
            serviceId: service.id,
            name: part.name,
            type: part.type,
            number: part.number,
            reference: part.reference,
            quantity: part.quantity,
            price: part.price,
            discount: part.discount || 0,
            costPrice: part.costPrice,
            margin: part.margin,
            vat: tenantVat,
            inventoryId: part.inventoryId,
          }))
          .filter((x: any) => x.name) || [],
    }));
  }

  async saveCurrentOrder(serviceStore: {
    selectedServices: any[];
    selectedServiceParts: Map<number, any[]>;
    tenantVat: number;
  }): Promise<Order | undefined> {
    if (!this.order) return undefined;

    const { selectedServices } = serviceStore;
    const order = this.order;

    const request: any = {
      id: order.id,
      endAt: order.endAt,
      title: selectedServices.map((s: any) => s.name).join(', '),
      duration: selectedServices.reduce((acc: number, curr: any) => acc + curr.duration, 0),
      workStationId: order.workStationId,
      userId: order.userId?.toString(),
      // The card shows the whole job, so an empty technician or workstation on it is a cleared
      // one - and the API takes a missing id as "unchanged", so the clearing must be said outright.
      clearTechnician: !order.userId,
      clearWorkStation: !order.workStationId,
      mileage: order.mileage,
      dropOffDate: order.dropOffDate,
      status: order.status,
      startAt: order.startAt,
      serviceOrders: this.buildServiceOrders(serviceStore, order.id),
      ...((order as any).restoreInventory !== undefined && {
        restoreInventory: (order as any).restoreInventory,
      }),
    };

    const result = await this.update(request);
    if (result) {
      this.setOrder(result);
      this.applySavedOrderToCalendar(result);
      this.syncAvailableStockFromOrder(result, serviceStore);
    }
    return result;
  }

  // The calendar's copy of a job otherwise waits for the OrderUpdated push, which can lag well
  // behind the save (ABP hands a notification for more than a few users to a background job) -
  // a job switched to TBC from the drawer reached the day's TBC row only on reload. So the save
  // result is applied straight away, routed by date like the push handler: dated jobs live on
  // the calendar, the rest in the side panel.
  applySavedOrderToCalendar = (saved: Order) => {
    this.applySavedOrderToPanel(saved);
    if (!saved.startAt) {
      this.removeCalendarOrder(saved.id);
      return;
    }
    const current = this.calendarOrders.get(saved.id);
    this.addOrUpdateCalendarOrder({
      ...current,
      id: saved.id,
      title: saved.title,
      status: saved.status,
      startAt: saved.startAt,
      endAt: saved.endAt,
      duration: saved.duration ?? current?.duration,
      dropOffDate: saved.dropOffDate,
      mileage: saved.mileage,
      inProgressMinutes: saved.inProgressMinutes,
      inProgressStartedAt: saved.inProgressStartedAt,
      registrationNumber: saved.registrationNumber ?? current?.registrationNumber,
      vehicleModel: saved.vehicleModel ?? current?.vehicleModel,
      fullName: saved.customer?.fullname ?? current?.fullName ?? '',
      // A cleared technician/workstation must not keep the old name from the calendar's copy.
      userId: saved.userId,
      userName: saved.userId ? (saved.user?.fullname ?? current?.userName ?? '') : '',
      workStationId: saved.workStationId,
      workStationName: saved.workStationId
        ? (saved.workStation?.name ?? current?.workStationName)
        : undefined,
    });
  };

  /**
   * The panel's side of a save: refresh the card, add it (a technician cleared from the drawer
   * puts the job on the Unassigned list) or take it off (a day or a technician given) - whichever
   * the saved job now calls for. Straight away, not on the push, for the same reason as above.
   */
  applySavedOrderToPanel = (saved: Order) =>
    this.applyOrderToPanel(toPanelCard(saved, this.unAssignedItems.get(saved.id)));

  /** Which list the panel is showing - the API's default when the request never said. */
  private get unAssignedScope(): UnassignedOrderScope {
    return this.lastUnAssignedRequest.scope ?? 'Unscheduled';
  }

  /**
   * Whether a job belongs on the list the panel is showing: a status still waiting to be worked,
   * and either no day yet (Unscheduled) or a day with nobody to do it (Unassigned). The same rule
   * the API applies, so a card arriving by push or save lands where a reload would put it.
   */
  private belongsOnPanel = (order: PanelCandidate) =>
    PENDING_SCHEDULING_STATUSES.includes(order.status) &&
    (this.unAssignedScope === 'Unassigned' ? !!order.startAt && !order.userId : !order.startAt);

  /**
   * Routes a changed job - a save result or a push - onto or off the panel's current list. A job
   * that stops belonging (given a day, given a technician, finished) goes; one that starts
   * belonging (day cleared, technician cleared) arrives; one already there is refreshed.
   */
  applyOrderToPanel = (order: UnassignedOrder & PanelCandidate) => {
    if (this.belongsOnPanel(order)) this.addUnassignedServiceOrder(order);
    else this.removeUnassignedServiceOrder(order.id);
  };

  private syncAvailableStockFromOrder(
    order: Order,
    serviceStore: { selectedServiceParts: Map<number, any[]> }
  ) {
    const byInventoryId = new Map<string, number>();
    order.serviceOrders?.forEach((so: any) => {
      so.extras?.forEach((extra: any) => {
        if (extra.inventoryId && typeof extra.availableStock === 'number') {
          byInventoryId.set(extra.inventoryId, extra.availableStock);
        }
      });
    });

    runInAction(() => {
      serviceStore.selectedServiceParts.forEach((parts) => {
        parts.forEach((part) => {
          if (part.inventoryId && byInventoryId.has(part.inventoryId)) {
            part.availableStock = byInventoryId.get(part.inventoryId);
          }
        });
      });
    });
  }

  async update(request: UpdateOrder): Promise<Order | undefined> {
    this.load(true);
    try {
      const result = await Services.OrderService.update(request);

      this.load(false);

      message.success({
        content: 'Successfully updated!',
      });

      return result;
    } catch (error) {
      this.load(false);
      console.error(error);
      return undefined;
    }
  }

  async updateStatus(request: UpdateOrder): Promise<Order | undefined> {
    this.setStatusLoading(true);
    try {
      const result = await Services.OrderService.update(request);

      this.setStatusLoading(false);

      message.success({
        content: 'Successfully updated!',
      });

      return result;
    } catch (error) {
      this.setStatusLoading(false);
      console.error(error);
      return undefined;
    }
  }

  async updateCalendar(request: UpdateOrder): Promise<Order | undefined> {
    this.setCalendarLoading(true);
    try {
      const result = await Services.OrderService.updateCalendar(request);
      this.setCalendarLoading(false);
      message.success({
        content: 'Successfully updated!',
      });

      return result;
    } catch (error) {
      this.setCalendarLoading(false);
      console.error(error);
      return undefined;
    }
  }

  //refactored
  async getUnAssignedItems(request: PagedUnassignedOrderRequest) {
    this.lastUnAssignedRequest = request;
    this.setUnAssignedLoading(true);
    this.setUnAssignedError(false);
    try {
      const result = await Services.OrderService.getAllUnassigned(request);

      runInAction(() => {
        result.items.forEach((item) => this.unAssignedItems.set(item.id, item));
        this.unAssignedTotalCount = result.totalCount;
      });
      this.setUnAssignedLoading(false);
    } catch (error) {
      this.setUnAssignedLoading(false);
      this.setUnAssignedError(true);
      console.error(error);
    }
  }

  /**
   * The panel's own two numbers, counted under whatever it is filtered by: a tab reading nine over
   * a list of four was taken as the list having lost cards. Called without a filter - by a push, or
   * by a card leaving one of the lists - it re-asks the last one used, so a background refresh
   * never quietly widens them.
   */
  async getUnAssignedCount(filter?: UnassignedOrderCountsRequest) {
    if (filter) this.lastUnAssignedCountFilter = filter;
    // Copied out of the store: what is held there is an observable, and axios is handed a plain
    // object. The key is compared instead of the object for the same reason - identity does not
    // survive the store.
    const request = { ...this.lastUnAssignedCountFilter };
    const key = JSON.stringify(request);
    this.unAssignedCountRequestKey = key;
    try {
      const counts = await Services.OrderService.getUnassignedCounts(request);
      // Narrowing the filter is faster than answering it, so the older, wider count can come back
      // last. Only the answer to the question still being asked is kept.
      if (this.unAssignedCountRequestKey !== key) return;
      runInAction(() => {
        this.unAssignedCounts = counts;
      });
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * The Jobs badge: everything still waiting, whatever the panel is filtered to. It is how much
   * work the garage has left, not how much of it is on screen, so it is asked for without filters.
   */
  async getUnAssignedTotals() {
    this.unAssignedTotalLoading = true;
    try {
      const counts = await Services.OrderService.getUnassignedCounts();
      runInAction(() => {
        this.unAssignedTotals = counts;
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.unAssignedTotalLoading = false;
      });
    }
  }

  /** Both badges at once, for the things that change what is waiting: a push, a card leaving. */
  refreshUnAssignedCounts = () => {
    // The filtered pair only where it is shown: nothing has asked for it if the panel's scope
    // switch has never been on screen, and there is then nothing to keep up to date.
    if (this.unAssignedCountRequestKey) this.getUnAssignedCount();
    this.getUnAssignedTotals();
  };

  //refactored
  async get(entityDto: Services.EntityDto<string>) {
    this.load(true);
    try {
      const result = await Services.OrderService.get(entityDto);
      runInAction(() => {
        this.order = result;
        this.savedDropOffDate = result.dropOffDate ? String(result.dropOffDate) : undefined;
      });
      this.load(false);
    } catch (error) {
      this.load(false);
      console.error(error);
    }
  }

  async getPresences(request: Services.PagedOrderPresenceRequest) {
    this.setPresenceLoading(true);
    try {
      const result = await Services.OrderService.getPresences(request);
      runInAction(() => {
        result.items.forEach((x) => this.presences.set(x.id, x));
      });
      this.setPresenceLoading(false);
    } catch (error) {
      this.setPresenceLoading(false);
      console.error(error);
    }
  }

  async createPresence(request: Services.OrderPresenceRequest) {
    try {
      await Services.OrderService.createPresence(request);
    } catch (error) {
      console.error(error);
    }
  }

  async deletePresence(request: Services.OrderPresenceRequest) {
    try {
      await Services.OrderService.deletePresence({ id: request.orderId });
    } catch (error) {
      console.error(error);
    }
  }

  async getRelatedOrders(entityDto: Services.EntityDto<string>) {
    this.setRelatedLoading(true);
    try {
      const result = await Services.OrderService.getRelatedOrders(entityDto);
      runInAction(() => {
        // Replace, not merge: the list is one order's.
        this.relatedOrders.clear();
        result.forEach((x) => this.relatedOrders.set(x.id, x));
        this.relatedOrdersOrderId = entityDto.id;
      });
      this.setRelatedLoading(false);
    } catch (error) {
      this.setRelatedLoading(false);
      console.error(error);
    }
  }

  //refactored
  async createServiceOrder(request: CreateServiceOrder) {
    this.load(true);
    try {
      const result = await Services.OrderService.createServiceOrder(request);

      this.load(false);

      message.success({
        content: 'Successfully created!',
      });

      return result;
    } catch (error) {
      this.load(false);
      return null;
    }
  }

  //refactored
  async updateServiceOrder(request: UpdateServiceOrder): Promise<ServiceOrder | undefined> {
    this.load(true);
    try {
      const result = await Services.OrderService.updateServiceOrder(request);
      const serviceOrder = this.order?.serviceOrders.find((x) => x.id === result.id);
      runInAction(() => {
        if (serviceOrder) {
          serviceOrder.price = result.price;
          serviceOrder.duration = result.duration;
          serviceOrder.discount = result.discount;
        }
      });
      this.load(false);

      message.success({
        content: 'Successfully updated!',
      });

      return result;
    } catch (error) {
      this.load(false);
      console.error(error);
      return undefined;
    }
  }

  //refactored
  async deleteServiceOrder(entityDto: Services.EntityDto<string>) {
    this.load(true);
    try {
      await Services.OrderService.deleteServiceOrder(entityDto);

      runInAction(() => {
        if (this.order) {
          const filtered = this.order?.serviceOrders.filter((x) => x.id !== entityDto.id);
          this.order.serviceOrders = filtered;
          this.order.lastModificationTime = new Date();
        }
      });

      this.load(false);

      message.success({
        content: 'Successfully deleted!',
      });
    } catch (error) {
      this.load(false);
      console.error(error);
      return [];
    }
  }

  //refactored
  async createExtra(request: Services.ServiceOrderExtra) {
    this.load(true);
    try {
      const result = await Services.OrderService.createExtra(request);

      this.load(false);

      message.success({
        content: 'Successfully created!',
      });
      return result;
    } catch (error) {
      this.load(false);
      return null;
    }
  }

  //refactored
  async updateExtra(serviceOrderExtra: Services.ServiceOrderExtra) {
    this.load(true);
    try {
      const result = await Services.OrderService.updateExtra(serviceOrderExtra);
      const extra = this.order?.serviceOrders.find(
        (x) => x.id === serviceOrderExtra.serviceOrderId
      )?.extras;
      runInAction(() => {
        if (extra) {
          const index = extra.findIndex((x) => x.id === result.id);
          extra[index] = result;
        }
      });
      this.load(false);
      message.success({
        content: 'Successfully updated!',
      });
      return result;
    } catch (error) {
      this.load(false);
      return null;
    }
  }

  async returnExtraParts(
    items: { id: string; destination: string; quantity?: number; documentId?: string }[]
  ) {
    this.load(true);
    try {
      const results = await Services.OrderService.returnExtraParts(items);

      runInAction(() => {
        results.forEach((result) => {
          const extras = this.order?.serviceOrders.find(
            (x) => x.id === result.serviceOrderId
          )?.extras;
          if (!extras) return;
          const index = extras.findIndex((x) => x.id === result.id);
          if (index === -1) return;
          // Partial return → line keeps the remaining qty; full return → line removed.
          if (result.quantity > 0) extras[index] = result;
          else extras.splice(index, 1);
        });
      });

      this.load(false);
      return results;
    } catch (error) {
      this.load(false);
      console.error(error);
      return null;
    }
  }

  //refactored
  async deleteExtra(entityDto: Services.EntityDto<string>, serviceOrderId: string) {
    this.load(true);
    try {
      await Services.OrderService.deleteExtra(entityDto);

      runInAction(() => {
        this.order?.serviceOrders.forEach((x) => {
          if (x.id === serviceOrderId) {
            x.extras = x.extras.filter((y) => y.id !== entityDto.id);
          }
        });
      });

      this.load(false);

      message.success({
        content: 'Successfully deleted!',
      });
      return Array.from(this.order?.serviceOrders ?? []);
    } catch (error) {
      this.load(false);
      console.error(error);
      return [];
    }
  }

  //refactored
  resetOrder = () => {
    runInAction(() => {
      this.order = undefined;
      // The related list and its ticks belong to the order that just closed; the next one must
      // not show them for the moment before its own fetch lands.
      this.relatedOrders.clear();
      this.relatedCheckedIds = [];
      this.relatedOrdersOrderId = undefined;
    });
  };

  resetPresences = () => {
    runInAction(() => {
      this.presences.clear();
    });
  };

  clearRelatedOrders = () => {
    runInAction(() => {
      this.relatedOrders.clear();
      this.relatedCheckedIds = [];
    });
  };

  // Called from the store's own list consumers: only the ticks are per-order state worth keeping
  // between the invoice card (always mounted) and the Related work drawer (mounted on demand).
  relatedOrderChecked = (id: string) => this.relatedCheckedIds.includes(id);

  toggleRelatedCheck = (id: string, checked: boolean) => {
    runInAction(() => {
      this.relatedCheckedIds = checked
        ? [...this.relatedCheckedIds, id]
        : this.relatedCheckedIds.filter((x) => x !== id);
    });
  };

  //refactored
  clearOrderChanges = () => {
    runInAction(() => {
      this.orderChanges.items = [];
      this.orderChanges.totalCount = 0;
    });
  };
  //refactored
  load(value: boolean) {
    runInAction(() => {
      this.loading = value;
    });
  }

  setOrdersError(value: boolean) {
    runInAction(() => {
      this.ordersError = value;
    });
  }

  setOrderNotesLoading(value: boolean) {
    runInAction(() => {
      this.orderNotesLoading = value;
    });
  }

  setOrderNoteLoading(value: boolean) {
    runInAction(() => {
      this.orderNoteLoading = value;
    });
  }

  setRelatedLoading(value: boolean) {
    runInAction(() => {
      this.relatedLoading = value;
    });
  }
  //refactored
  setCalendarLoading(value: boolean) {
    runInAction(() => {
      this.calendarLoading = value;
    });
  }
  //refactored
  setUnAssignedLoading(value: boolean) {
    runInAction(() => {
      this.unAssignedLoading = value;
    });
  }

  setUnAssignedError(value: boolean) {
    runInAction(() => {
      this.unAssignedError = value;
    });
  }

  async getAll(pagedFilterAndSortedRequest: Services.PagedServiceOrderRequest) {
    this.load(true);
    this.setOrdersError(false);
    try {
      const result = await Services.OrderService.getAll(pagedFilterAndSortedRequest);

      runInAction(() => {
        this.orders.clear();
        this.totalCount = result.totalCount;
        result.items.forEach((x) => this.orders.set(x.id, x));
      });
      this.load(false);
    } catch (error) {
      this.load(false);
      // The list the table is holding says nothing about what the request was asking for, so it
      // goes: a stale page of jobs under a filter that never ran is worse than none.
      runInAction(() => {
        this.orders.clear();
        this.totalCount = 0;
      });
      this.setOrdersError(true);
      console.error(error);
    }
  }


  async getOrderNotes(request: Services.PagedOrderNotes) {
    this.setOrderNotesLoading(true);
    try {
      const result = await Services.OrderService.getOrderNotes(request);

      runInAction(() => {
        this.orderNotesTotalCount = result.totalCount;
        result.items.forEach((x) => this.orderNotes.set(x.id, x));
      });
      this.setOrderNotesLoading(false);
    } catch (error) {
      this.setOrderNotesLoading(false);
      console.error(error);
    }
  }

  async createOrderNote(request: Services.CreateOrUpdateOrderNote) {
    this.setOrderNoteLoading(true);
    try {
      const result = await Services.OrderService.createOrderNote(request);

      runInAction(() => {
        this.orderNotes.set(result.id, result);
        this.orderNotesTotalCount += 1;
        if (this.order?.id === result.orderId) this.order.noteCount += 1;
        // Sort the order notes by creation time in descending order
        this.orderNotes = new Map(
          Array.from(this.orderNotes.entries()).sort(
            (a, b) => new Date(b[1].creationTime).getTime() - new Date(a[1].creationTime).getTime()
          )
        );

        // If totalCount exceeds maxResultCount, remove the last one
        if (this.orderNotesTotalCount > this.orderNotesMaxResultCount) {
          const lastKey = Array.from(this.orderNotes.keys()).pop();
          if (lastKey) {
            this.orderNotes.delete(lastKey);
          }
        }
      });
      this.setOrderNoteLoading(false);
    } catch (error) {
      this.setOrderNoteLoading(false);
      console.error(error);
    }
  }

  async updateOrderNote(request: Services.CreateOrUpdateOrderNote) {
    this.setOrderNotesLoading(true);
    try {
      const result = await Services.OrderService.updateOrderNote(request);

      runInAction(() => {
        const orderNote = this.orderNotes.get(result.id);
        if (orderNote) {
          orderNote.description = result.description;
          orderNote.lastModificationTime = result.lastModificationTime;
          this.orderNotes.set(result.id, orderNote);
        }
      });
      this.setOrderNotesLoading(false);
    } catch (error) {
      this.setOrderNotesLoading(false);
      console.error(error);
    }
  }

  // The work log tab totals the entries itself (overall, per technician, per service line), so a
  // page would give a total of the page - wrong, not merely partial. It therefore loads the whole
  // set, following totalCount rather than trusting one request to have returned everything.
  async getOrderWorkLogs(orderId: string) {
    const pageSize = 200;
    runInAction(() => {
      this.orderWorkLogsLoading = true;
    });
    try {
      const loaded: Services.OrderWorkLog[] = [];
      let totalCount = 0;

      do {
        // eslint-disable-next-line no-await-in-loop
        const page = await Services.OrderService.getWorkLogs({
          orderId,
          skipCount: loaded.length,
          maxResultCount: pageSize,
        });
        totalCount = page.totalCount;
        loaded.push(...page.items);
        // A page that comes back empty while the count says otherwise would loop forever.
        if (page.items.length === 0) break;
      } while (loaded.length < totalCount);

      runInAction(() => {
        this.orderWorkLogs.clear();
        loaded.forEach((x) => this.orderWorkLogs.set(x.id, x));
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.orderWorkLogsLoading = false;
      });
    }
  }

  // Create/update/delete rethrow so the form stays open on a rejected save (e.g. a technician
  // logging time for someone else gets a 403 that the http interceptor already reports).
  async createOrderWorkLog(request: Services.CreateOrderWorkLog) {
    runInAction(() => {
      this.orderWorkLogSaving = true;
    });
    try {
      const result = await Services.OrderService.createWorkLog(request);
      runInAction(() => {
        this.orderWorkLogs.set(result.id, result);
        this.syncLoggedMinutes();
      });
      return result;
    } finally {
      runInAction(() => {
        this.orderWorkLogSaving = false;
      });
    }
  }

  async updateOrderWorkLog(request: Services.UpdateOrderWorkLog) {
    runInAction(() => {
      this.orderWorkLogSaving = true;
    });
    try {
      const result = await Services.OrderService.updateWorkLog(request);
      runInAction(() => {
        this.orderWorkLogs.set(result.id, result);
        this.syncLoggedMinutes();
      });
      return result;
    } finally {
      runInAction(() => {
        this.orderWorkLogSaving = false;
      });
    }
  }

  async deleteOrderWorkLog(id: string) {
    await Services.OrderService.deleteWorkLog({ id });
    runInAction(() => {
      this.orderWorkLogs.delete(id);
      this.syncLoggedMinutes();
    });
  }

  resetOrderWorkLogs = () => {
    runInAction(() => {
      this.orderWorkLogs.clear();
    });
  };

  // A build leg's read-only logged time comes from the API with the order; after an entry is
  // added, edited or removed on the tab the card would otherwise show the old total until the
  // next fetch. Only the open order is touched - the loaded entries always belong to it.
  private syncLoggedMinutes() {
    if (!this.order) return;
    const orderId = this.order.id;
    const logs = Array.from(this.orderWorkLogs.values()).filter((x) => x.orderId === orderId);
    this.order.loggedMinutes = logs.reduce((acc, log) => acc + workLogMinutes(log), 0);
  }

  async getApprovalHistory(request: Services.PagedOrderApprovalHistoryRequest) {
    this.setApprovalHistoryLoading(true);
    try {
      const result = await Services.OrderService.getApprovalHistory(request);

      runInAction(() => {
        this.approvalHistoryTotalCount = result.totalCount;
        result.items.forEach((x) => this.approvalHistory.set(x.id, x));
      });
      this.setApprovalHistoryLoading(false);
    } catch (error) {
      this.setApprovalHistoryLoading(false);
      console.error(error);
    }
  }

  async updateApprovalHistory(request: Services.UpdateOrderApprovalHistoryRequest) {
    this.setApprovalHistoryLoading(true);
    try {
      const result = await Services.OrderService.updateApprovalHistory(request);
      runInAction(() => {
        this.approvalHistory.set(result.id, result);
      });
      this.setApprovalHistoryLoading(false);
      message.success('Approval updated successfully');
      return result;
    } catch (error) {
      this.setApprovalHistoryLoading(false);
      console.error(error);
    }
  }

  async reviseApproval(request: Services.ReviseOrderApprovalRequest) {
    this.setApprovalHistoryLoading(true);
    try {
      const { approval, order } = await Services.OrderService.reviseApproval(request);
      runInAction(() => {
        const original = this.approvalHistory.get(request.id);
        if (original) {
          this.approvalHistory.set(request.id, {
            ...original,
            status: 'Superseded',
            supersededByApprovalId: approval.id,
          });
        }
        this.approvalHistory.set(approval.id, approval);
        this.order = order;
      });
      this.setApprovalHistoryLoading(false);
      message.success('Estimate revised');
      return approval;
    } catch (error) {
      this.setApprovalHistoryLoading(false);
      console.error(error);
      throw error;
    }
  }

  async getVehicleHistoryOrders(request: Services.PagedVehicleHistoryOrdersRequest) {
    this.load(true);
    try {
      const result = await Services.OrderService.getVehicleHistoryOrders(request);

      runInAction(() => {
        this.vehicleHistoryOrdersTotalCount = result.totalCount;
        result.items.forEach((x) => this.vehicleHistoryOrders.set(x.id, x));
      });
      this.load(false);
    } catch (error) {
      this.load(false);
      console.error(error);
    }
  }

  async getGenerateInvoiceOrders(ids: string[]) {
    this.load(true);
    try {
      const result = await Services.OrderService.getGenerateInvoiceOrders(ids);

      runInAction(() => {
        this.generateInvoiceOrders.clear();
        result.forEach((x) => this.generateInvoiceOrders.set(x.id, x));
      });
      this.load(false);
    } catch (error) {
      this.load(false);
      console.error(error);
    }
  }

  async delete(entityDto: Services.EntityDto<string>) {
    this.load(true);
    try {
      await Services.OrderService.delete(entityDto);
      runInAction(() => {
        this.orders.delete(entityDto.id);
      });
      this.load(false);
    } catch (error) {
      this.load(false);
      console.error(error);
    }
  }

  async getAllChanges(pagedFilterAndSortedRequest: Services.PagedOrderChange) {
    const result = await Services.OrderService.getAllChanges(pagedFilterAndSortedRequest);
    runInAction(() => {
      this.orderChanges = result;
    });
  }

  async getAllDocuments(pagedFilterAndSortedRequest: Services.PagedDocumentRequest) {
    const result = await Services.OrderService.getAllDocuments(pagedFilterAndSortedRequest);
    runInAction(() => {
      this.serviceOrderDocuments = result;
    });
  }

  async deleteDocument(entityDto: Services.EntityDto<string>) {
    await Services.OrderService.deleteDocument(entityDto);
    runInAction(() => {
      this.serviceOrderDocuments.items = this.serviceOrderDocuments.items.filter(
        (x) => x.id !== entityDto.id
      );
      this.serviceOrderDocuments.totalCount -= 1;
    });
  }

  async deleteUnAssignedItem(entityDto: Services.EntityDto<string>) {
    this.setUnAssignedLoading(true);
    try {
      await Services.OrderService.delete(entityDto);
      this.setUnAssignedLoading(false);
      message.success({
        content: 'Successfully deleted!',
      });
    } catch (error) {
      this.setUnAssignedLoading(false);
    }
  }

  async deleteCalendarItem(entityDto: Services.EntityDto<string>) {
    this.setCalendarLoading(true);
    try {
      await Services.OrderService.delete(entityDto);
      this.setCalendarLoading(false);
      message.success({
        content: 'Successfully deleted!',
      });
    } catch (error) {
      this.setCalendarLoading(false);
    }
  }

  async unassignCalendarItem(entityDto: Services.EntityDto<string>) {
    this.setCalendarLoading(true);
    try {
      await Services.OrderService.unassign(entityDto);
      this.setCalendarLoading(false);
      message.success({
        content: 'Successfully unassigned!',
      });
      return true;
    } catch (error) {
      this.setCalendarLoading(false);
      return null;
    }
  }

  async createDocument(input: Services.CreateServiceOrderDocument) {
    try {
      const result = await Services.OrderService.createDocument(input);
      runInAction(() => {
        this.serviceOrderDocuments.items = [...this.serviceOrderDocuments.items, result];
        this.serviceOrderDocuments.totalCount += 1;
      });
      message.success({
        content: 'Successfully uploaded!',
      });
      return result;
    } catch (error) {
      return null;
    }
  }

  handlePageChange = (pagination: any) => {
    runInAction(() => {
      this.maxResultCount = pagination.pageSize;
      this.skipCount = (pagination.current - 1) * pagination.pageSize;
      this.currentPage = pagination.current;
    });
  };

  handleVehicleHistoryPageChange = (pagination: any) => {
    runInAction(() => {
      this.vehicleHistoryOrdersMaxResultCount = pagination.pageSize;
      this.vehicleHistoryOrdersSkipCount = (pagination.current - 1) * pagination.pageSize;
      this.vehicleHistoryOrdersCurrentPage = pagination.current;
    });
  };

  handleOrderNotesPageChange = (pagination: any) => {
    runInAction(() => {
      this.orderNotesSkipCount = (pagination - 1) * this.orderNotesMaxResultCount;
      this.orderNotesCurrentPage = pagination;
    });
  };

  setSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setVehicleHistorySkipCount = (value: number) => {
    runInAction(() => {
      this.vehicleHistoryOrdersSkipCount = value;
    });
  };

  setCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  setOrderNotesCurrentPage = (value: number) => {
    runInAction(() => {
      this.orderNotesCurrentPage = value;
    });
  };

  setVehicleHistoryCurrentPage = (value: number) => {
    runInAction(() => {
      this.vehicleHistoryOrdersCurrentPage = value;
    });
  };

  setChangeSkipCount = (value: number) => {
    runInAction(() => {
      this.skipCount = value;
    });
  };

  setChangeCurrentPage = (value: number) => {
    runInAction(() => {
      this.currentPage = value;
    });
  };

  setApprovalHistoryLoading(value: boolean) {
    runInAction(() => {
      this.approvalHistoryLoading = value;
    });
  }

  handleApprovalHistoryPageChange = (pagination: any) => {
    runInAction(() => {
      this.approvalHistoryMaxResultCount = pagination.pageSize;
      this.approvalHistorySkipCount = (pagination.current - 1) * pagination.pageSize;
      this.approvalHistoryCurrentPage = pagination.current;
    });
  };

  clearApprovalHistory = () => {
    runInAction(() => {
      this.approvalHistory.clear();
      this.approvalHistoryTotalCount = 0;
    });
  };

  resetApprovalHistory = () => {
    runInAction(() => {
      this.approvalHistory.clear();
      this.approvalHistoryTotalCount = 0;
      this.approvalHistorySkipCount = 0;
      this.approvalHistoryCurrentPage = 1;
    });
  };

  setPresenceLoading = (value: boolean) => {
    runInAction(() => {
      this.presenceLoading = value;
    });
  };

  clearOrders = () => {
    runInAction(() => {
      this.orders.clear();
    });
  };

  resetOrders = () => {
    runInAction(() => {
      this.orders.clear();
      this.totalCount = 0;
      this.skipCount = 0;
      this.currentPage = 1;
      this.ordersError = false;
    });
  };

  clearVehicleHistoryOrders = () => {
    runInAction(() => {
      this.vehicleHistoryOrders.clear();
      this.vehicleHistoryOrdersTotalCount = 0;
    });
  };

  clearUnAssignedItems = () => {
    runInAction(() => {
      this.unAssignedItems.clear();
    });
  };

  clearOrderNotes = () => {
    runInAction(() => {
      this.orderNotes.clear();
      this.orderNotesTotalCount = 0;
    });
  };

  resetOrderNotes = () => {
    runInAction(() => {
      this.orderNotes.clear();
      this.orderNotesTotalCount = 0;
      this.orderNotesSkipCount = 0;
      this.orderNotesCurrentPage = 1;
    });
  };

  setOrderNotesSkipCount = (value: number) => {
    runInAction(() => {
      this.orderNotesSkipCount = value;
    });
  };

  setUnAssignedSkipCount = (value: number) => {
    runInAction(() => {
      this.unAssignedSkipCount = value;
    });
  };

  setUnassignedPageChange = (current: number) => {
    runInAction(() => {
      this.unAssignedSkipCount = (current - 1) * this.unAssignedMaxResultCount;
      this.unassignedCurrentPage = current;
    });
  };

  /** True when the panel is showing something narrower than every unassigned card. */
  private get isUnAssignedFiltered() {
    const r = this.lastUnAssignedRequest;
    return !!(
      r.registrationNumber ||
      r.vin ||
      r.customer ||
      r.build ||
      r.status ||
      r.dateFrom ||
      r.dateTo ||
      (r.kind && r.kind !== 'All')
    );
  }

  addUnassignedServiceOrder = (serviceOrder: UnassignedOrder) => {
    // Already on the panel (an OrderUpdated push for a waiting job): refresh the card in place. It
    // used to be unshifted as well, and the stale copy further down then won the final set.
    // Merged, not replaced, for the same reason the calendar merges: the push carries what its
    // handler lists, which is not the build fields, and a replace stripped the build badge.
    const current = this.unAssignedItems.get(serviceOrder.id);
    if (current) {
      runInAction(() => this.unAssignedItems.set(serviceOrder.id, { ...current, ...serviceOrder }));
      return;
    }

    this.refreshUnAssignedCounts();

    // A card arriving over SignalR has not been through the filter, so it cannot just be put
    // at the top of a filtered list - re-ask instead, and let the server decide if it belongs.
    if (this.isUnAssignedFiltered) {
      this.getUnAssignedItems(this.lastUnAssignedRequest);
      return;
    }

    runInAction(() => {
      const entries = Array.from(this.unAssignedItems.entries());
      entries.unshift([serviceOrder.id, serviceOrder]);

      if (entries.length > this.unAssignedMaxResultCount) {
        entries.pop();
      }

      this.unAssignedItems.clear();
      entries.forEach(([id, order]) => this.unAssignedItems.set(id, order));
    });
  };

  removeCalendarOrder = (id: string) => {
    runInAction(() => {
      this.calendarOrders.delete(id);
    });
  };

  removeUnassignedServiceOrder = async (id: string) => {
    const totalCount = this.unAssignedTotalCount;

    // Not listed: nothing to take off. The badges are re-asked rather than decremented, because
    // a job leaving one list has often just joined the other (given a day, or a technician).
    if (!this.unAssignedItems.has(id)) {
      this.refreshUnAssignedCounts();
      return;
    }

    runInAction(() => {
      this.unAssignedItems.delete(id);
      this.unAssignedTotalCount -= 1;
    });
    this.refreshUnAssignedCounts();

    if (this.unAssignedItems.size === 0 && this.unassignedCurrentPage > 1) {
      this.setUnassignedPageChange(this.unassignedCurrentPage - 1);
    }

    const currentCount = this.unAssignedMaxResultCount * this.unassignedCurrentPage;

    if (totalCount <= currentCount || this.unAssignedTotalCount === 0) return;

    // Carries the panel's filters through, or backfilling the page would pull in rows the
    // user has filtered out.
    await this.getUnAssignedItems({
      ...this.lastUnAssignedRequest,
      skipCount:
        totalCount === this.unAssignedSkipCount + 1
          ? this.unAssignedSkipCount - this.unAssignedMaxResultCount
          : this.unAssignedSkipCount,
      maxResultCount: this.unAssignedMaxResultCount,
    });
  };

  onServiceOrderChangeNotification = (serviceOrder: any) => {
    if (!this.calendarOrders.has(serviceOrder.id)) {
      runInAction(() => {
        this.calendarOrders.set(serviceOrder.id, {
          ...serviceOrder,
          service: { name: serviceOrder.serviceName },
          customer: { fullname: serviceOrder.customerFullName },
          vehicle: { registrationNumber: serviceOrder.registrationNumber },
        });
        this.calendarTotalCount += 1;
      });
    } else {
      runInAction(() => {
        this.calendarOrders.set(serviceOrder.id, {
          ...serviceOrder,
          serviceOrderWorkStation: {
            serviceOrderId: serviceOrder.id,
            workStationId: serviceOrder.workStationId,
            workStation: {
              id: serviceOrder.workStationId,
              name: serviceOrder.workStation,
              tenantProfileId: serviceOrder.tenantId,
              isSelected: true,
            },
          },
        });
      });
    }
  };
}

export default OrderStore;
